import { PdpScoreStatus, PdpStatus } from '@vizit/api';
import { Pill, PillLevel, ScoreSticker, ScoreStickerSize, Tooltip, TooltipTrigger } from '@vizit/atoms';
import { cx } from '@vizit/theme';
import { formatDate } from '@vizit/utils';
import React from 'react';
import { Button } from 'react-aria-components';
import type { ProductListingData } from './product-listing-row';

/**
 * Checks if a product was recently refreshed (within the last 7 days)
 */
function wasRecentlyRefreshed(lastRefreshed?: Date | null): lastRefreshed is Date {
  if (!lastRefreshed) return false;
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  return lastRefreshed > sevenDaysAgo;
}

function lastRefreshedAt(item: ProductListingData): Date | null {
  return item.lastRefreshed ?? item.lastScraped ?? item.createdAt ?? null;
}

export interface ProductListingRowScoreProps {
  item: ProductListingData;
}

const ProductListingRowScore = React.memo<ProductListingRowScoreProps>(({ item }) => {
  const lastRefreshedDate = lastRefreshedAt(item);
  const isRefreshEnabled = item.sourceType !== 'manual';
  const isRefreshing = isRefreshEnabled && item.status === PdpStatus.ScrapeStarted;

  switch (item.scoreStatus) {
    case PdpScoreStatus.Created:
      return <Pill level={PillLevel.Default}>Processing</Pill>;
    case PdpScoreStatus.Processing:
      return <Pill level={PillLevel.Default}>Processing</Pill>;
    case PdpScoreStatus.CategoryNotFound:
      return <Pill level={PillLevel.Error}>Error</Pill>;
    case PdpScoreStatus.RescorePending:
      return <Pill level={PillLevel.Default}>Processing</Pill>;
    case PdpScoreStatus.Scored:
      return (
        <div className="flex items-center justify-start">
          <ScoreSticker
            score={item.score ?? null}
            size={ScoreStickerSize.Xs}
            className={cx(isRefreshing && 'opacity-40 animate-pulse')}
          />
          {isRefreshEnabled && wasRecentlyRefreshed(lastRefreshedDate) && (
            <TooltipTrigger delay={500}>
              <Button
                className={cx(
                  'flex items-center outline-none w-12 h-12 justify-center',
                  isRefreshing && 'animate-pulse',
                )}
              >
                <div className="h-3 w-3 rounded-full bg-brand-600" aria-label="Recently refreshed" />
              </Button>
              <Tooltip>{isRefreshing ? 'Refreshing...' : `Last refresh: ${formatDate(lastRefreshedDate)}`}</Tooltip>
            </TooltipTrigger>
          )}
        </div>
      );

    default:
      return <Pill level={PillLevel.Default}>N/A</Pill>;
  }
});

ProductListingRowScore.displayName = 'ProductListingRowScore';

export { ProductListingRowScore };
